<!-- eslint-disable vue/max-attributes-per-line -->
<!--
  新クロージングページ（日報β専用）。
  ステップ: データセット管理 → 経費管理 → ユーザー集計 → 卓集計 → 店舗集計 → 完了。
  ユーザー集計／卓集計／店舗集計は、対応する集計表が 0 件のときステッパーから
  除外する（データセット管理・経費管理・完了は常に表示）。
  既存 eod-closing は温存し、本ページは別ルート（eod-closing-v2）として新規追加している。
  日計（ユーザー/卓/店舗）はスプレッドシート形式で、集計取得・保存・再集計は
  既存 eod-closing のAPI（useEodClosingV2Aggregations 経由）を流用する。
-->
<template>
  <div
    :key="`eod-closing-v2-${date}`"
    class="eod-closing-v2-page"
    :class="{ 'eod-closing-v2-page--summary-open': summaryOpen }"
  >
    <!-- スマホ幅用トグル（タブレット以上では非表示）。本日サマリーの開閉。 -->
    <button
      type="button"
      class="eod-closing-v2-page__summary-toggle"
      @click="summaryOpen = !summaryOpen"
    >
      <v-icon
        small
        left
      >
        {{ summaryOpen ? icons.mdiChevronUp : icons.mdiChevronDown }}
      </v-icon>
      本日サマリー
    </button>

    <!-- 左: 本日サマリーバー -->
    <eod-closing-summary-bar
      :date="date"
      :refresh-key="summaryRefreshKey"
      class="eod-closing-v2-page__summary"
    />

    <!-- 右: クロージング本体 -->
    <v-container class="eod-closing-v2-page__content">
      <h1 class="mb-4">
        {{ date }} クロージング
      </h1>

    <!-- 日付選択 -->
    <div class="d-flex align-center flex-wrap mb-12">
      <v-menu
        transition="scale-transition"
        min-width="auto"
      >
        <template #activator="{ on, attrs }">
          <v-text-field
            :value="date"
            :prepend-icon="icons.mdiCalendar"
            readonly
            hide-details
            style="max-width: 200px;"
            v-bind="attrs"
            v-on="on"
          />
        </template>
        <v-date-picker
          v-model="date"
          locale="ja"
          :day-format="d => new Date(d).getDate()"
          :max="isProduction ? today : undefined"
          color="primary"
          no-title
        />
      </v-menu>

      <v-btn
        color="secondary"
        small
        class="ml-3"
        :ripple="false"
        @click="date = today"
      >
        本日
      </v-btn>

      <v-btn
        color="info"
        small
        class="ml-2"
        :ripple="false"
        :to="{ name: 'daily-report-summary', query: { date } }"
      >
        日報
      </v-btn>
    </div>

    <!-- 天気・イベント内容・日報記入者（全ステップ共通で常時表示） -->
    <div class="record-section mb-4">
      <v-row>
        <v-col
          cols="12"
          md="4"
        >
          <daily-weather
            :key="`weather-${date}`"
            :date="date"
            :readonly="false"
          />
        </v-col>
        <v-col
          cols="12"
          md="8"
        >
          <daily-club-event
            :key="`event-${date}`"
            :date="date"
            :readonly="false"
          />
        </v-col>
      </v-row>
      <daily-report-closer
        :key="`closer-${date}`"
        :date="date"
        :readonly="false"
        class="mt-4"
      />
    </div>

    <!-- 読み込み中はステッパーを描画せず、最終形に近いスケルトンを表示する。
         確定した構成（集計表のあるステップのみ）を最初から出し、空ステップが後から
         消えるチラつきを防ぐ。 -->
    <div
      v-if="isLoading"
      class="eod-closing-v2-loading mt-8 mb-4"
    >
      <!-- ステッパーヘッダー帯（ステップの丸＋ラベルを模す） -->
      <div class="eod-closing-v2-loading__header mb-8">
        <div
          v-for="n in 5"
          :key="`skeleton-step-${n}`"
          class="eod-closing-v2-loading__step"
        >
          <v-skeleton-loader type="avatar" />
          <v-skeleton-loader
            type="text"
            class="eod-closing-v2-loading__step-label"
          />
        </div>
      </div>
      <!-- 集計表エリア -->
      <v-skeleton-loader type="heading" class="mb-4" />
      <v-skeleton-loader type="table-tbody" />
    </div>

    <!-- ステッパー -->
    <v-stepper
      v-else
      v-model="step"
      flat
      class="eod-closing-v2-stepper mt-8 mb-4"
    >
      <v-stepper-header>
        <template v-for="(stepDef, idx) in steps">
          <v-tooltip
            :key="`step-${stepDef.key}`"
            bottom
            :disabled="isStepEditable(idx)"
            content-class="eod-v2-step-tooltip"
          >
            <template #activator="{ on, attrs }">
              <!-- ホバー判定を持たせるための native ラッパー（v-stepper-step は -->
              <!-- コンポーネントのため v-on のリスナーが DOM に伝わらない）。 -->
              <div
                class="eod-v2-step-activator"
                v-bind="attrs"
                v-on="on"
                @click="cancelPendingStep"
              >
                <v-stepper-step
                  :class="categoryStatusClass(stepDef.key)"
                  :complete="categoryStatus(stepDef.key) ? true : currentStepIndex > idx"
                  :editable="isStepEditable(idx)"
                  :edit-icon="categoryIcon(stepDef.key) || '$complete'"
                  :complete-icon="categoryIcon(stepDef.key) || '$complete'"
                  :step="keyToStepValue(stepDef.key)"
                >
                  {{ stepDef.label }}
                </v-stepper-step>
              </div>
            </template>
            <span>{{ stepLockReason(idx) }}</span>
          </v-tooltip>
          <v-divider
            v-if="idx < steps.length - 1"
            :key="`step-divider-${stepDef.key}`"
          />
        </template>
      </v-stepper-header>

      <v-stepper-items>
        <v-stepper-content
          v-for="stepDef in steps"
          :key="`step-content-${stepDef.key}`"
          :step="keyToStepValue(stepDef.key)"
          eager
        >
          <h2
            v-if="!['user', 'table', 'club'].includes(stepDef.key)"
            class="mb-3"
          >
            {{ stepDef.label }}
          </h2>

          <!-- データセット管理 -->
          <template v-if="stepDef.key === 'dataset'">
            <timecards-table-inline
              :date="date"
              class="mb-6"
              @timecards-changed="onTimecardsChanged"
            />
            <data-tabs
              :show-date-picker="false"
              @changed="bumpSummary"
            />
          </template>

          <!-- 経費管理（諸経費 / 諸入費をタブで切り替え） -->
          <template v-else-if="stepDef.key === 'expense'">
            <v-tabs
              v-model="expenseTabIndex"
              background-color="transparent"
            >
              <v-tab>諸経費</v-tab>
              <v-tab>諸入費</v-tab>
            </v-tabs>
            <v-tabs-items v-model="expenseTabIndex">
              <v-tab-item>
                <expenses-table
                  :date="date"
                  @changed="bumpSummary"
                />
              </v-tab-item>
              <v-tab-item>
                <benefits-table
                  :key="`benefits-table-${date}`"
                  :date="date"
                />
              </v-tab-item>
            </v-tabs-items>
          </template>

          <!-- ユーザー集計 -->
          <daily-aggregation-step
            v-else-if="stepDef.key === 'user'"
            category-label="ユーザー集計"
            type="EodClosingUserAggregation"
            :date="date"
            :aggregations-data="userAggregationsData"
            :aggregations-map="userAggregationsMap"
            :job-statuses="jobStatuses"
            :job-error-messages="jobErrorMessages"
            :job-timestamps="jobTimestamps"
            :loading-statuses="loadingStatuses"
            :is-loading="isLoading"
            :status-resolver="sheetStatus"
            :available-users="availableUsers"
            :individual-completing-ids="individualCompletingIds"
            :bulk-status="bulkCompletionByType.EodClosingUserAggregation"
            @save-cell="onSaveCell"
            @bulk-complete="onBulkComplete"
            @bulk-complete-all="onBulkCompleteAll"
            @individual-complete="onIndividualComplete"
            @fetch-available-users="onFetchAvailableUsers"
            @create-user="onCreateUser"
            @destroy-user="onDestroyUser"
          />

          <!-- 卓集計 -->
          <daily-aggregation-step
            v-else-if="stepDef.key === 'table'"
            category-label="卓集計"
            type="EodClosingTableAggregation"
            :date="date"
            :aggregations-data="tableAggregationsData"
            :aggregations-map="tableAggregationsMap"
            :job-statuses="jobStatuses"
            :job-error-messages="jobErrorMessages"
            :job-timestamps="jobTimestamps"
            :loading-statuses="loadingStatuses"
            :is-loading="isLoading"
            :status-resolver="sheetStatus"
            :individual-completing-ids="individualCompletingIds"
            :bulk-status="bulkCompletionByType.EodClosingTableAggregation"
            @save-cell="onSaveCell"
            @bulk-complete="onBulkComplete"
            @bulk-complete-all="onBulkCompleteAll"
            @individual-complete="onIndividualComplete"
          />

          <!-- 店舗集計 -->
          <daily-aggregation-step
            v-else-if="stepDef.key === 'club'"
            category-label="店舗集計"
            type="EodClosingClubAggregation"
            :date="date"
            :aggregations-data="clubAggregationsData"
            :aggregations-map="clubAggregationsMap"
            :job-statuses="jobStatuses"
            :job-error-messages="jobErrorMessages"
            :job-timestamps="jobTimestamps"
            :loading-statuses="loadingStatuses"
            :is-loading="isLoading"
            :status-resolver="sheetStatus"
            :individual-completing-ids="individualCompletingIds"
            :bulk-status="bulkCompletionByType.EodClosingClubAggregation"
            @save-cell="onSaveCell"
            @bulk-complete="onBulkComplete"
            @bulk-complete-all="onBulkCompleteAll"
            @individual-complete="onIndividualComplete"
          />

          <!-- 完了 -->
          <eod-closing-complete-step
            v-else-if="stepDef.key === 'done'"
            :date="date"
            :completed="allClosingCompleted"
          />
        </v-stepper-content>
      </v-stepper-items>
    </v-stepper>

    <!--
      店舗集計の「完了する」押下時、上流（ユーザー集計・卓集計）が未保存・未完了の場合の案内。
      2026-06-02: サーバ側のクロスタイプ待ち合わせ装置を撤廃。上流確認はフロントが担う。
    -->
    <v-dialog
      v-model="upstreamDialog.open"
      max-width="480"
    >
      <v-card>
        <v-card-title class="text-h6">
          {{ upstreamDialog.state === 'in_progress'
            ? 'ユーザー集計・卓集計が実行中です'
            : 'ユーザー集計・卓集計を先に実行してください' }}
        </v-card-title>
        <v-card-text>
          <template v-if="upstreamDialog.state === 'in_progress'">
            実行中の集計が完了するまでお待ちください。完了してから「店舗集計を完了する」を押し直してください。
          </template>
          <template v-else>
            店舗集計は、ユーザー集計と卓集計の保存結果を元に計算されます。
            先にユーザー集計・卓集計のステップで「完了する」を押して保存を完了してください。
          </template>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            @click="upstreamDialog.open = false"
          >
            閉じる
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    </v-container>
  </div>
</template>

<script>
import {
  ref, inject, computed, watch, onMounted, onBeforeUnmount, getCurrentInstance,
} from '@vue/composition-api'
import {
  mdiCalendar, mdiAlert, mdiExclamationThick, mdiChevronDown, mdiChevronUp,
} from '@mdi/js'
import { parseISO, isAfter } from 'date-fns'
import { useRouter } from '@core/utils'
import { useProcessEnv } from '@/views/v2-temp/common/composables/useProcessEnv'
import { handleApiError } from '@/utils/errorHandler'
import TimecardsTableInline from './components/TimecardsTableInline.vue'
import ExpensesTable from '@/views/v2-temp/eod-closing/components/ExpensesTable.vue'
import DataTabs from '@/views/waiter/Data.vue'
import DailyAggregationStep from './components/DailyAggregationStep.vue'
import EodClosingCompleteStep from './components/EodClosingCompleteStep.vue'
import EodClosingSummaryBar from './components/EodClosingSummaryBar.vue'
import DailyWeather from '@/views/components/daily_report/Closing/DailyWeather.vue'
import DailyClubEvent from '@/views/components/daily_report/Closing/DailyClubEvent.vue'
import DailyReportCloser from '@/views/components/daily_report/Closing/DailyReportCloser.vue'
import BenefitsTable from './components/BenefitsTable.vue'
import useEodClosingV2Aggregations from './composables/useEodClosingV2Aggregations'

// カテゴリステップ（ユーザー/卓/店舗集計）のステータス -> ステップ丸のアイコン。
// 丸の色はステータス別クラス（.eod-v2-step--<status>）で CSS 側に持たせる。
//   - 未集計(uncounted)            : 赤 + ビックリマーク
//   - 未保存(unsaved)/要再集計      : 黄 + ワーニング
//   - 完了(done)                   : 白 + 既定のチェック（icon は変更しない → null）
const STEP_STATUS_ICON = {
  uncounted: mdiExclamationThick,
  unsaved: mdiAlert,
  needs_reaggregation: mdiAlert,
  done: null,
}

export default {
  name: 'EodClosingV2',
  components: {
    TimecardsTableInline,
    ExpensesTable,
    DataTabs,
    DailyAggregationStep,
    EodClosingCompleteStep,
    EodClosingSummaryBar,
    DailyWeather,
    DailyClubEvent,
    DailyReportCloser,
    BenefitsTable,
  },
  setup() {
    const vm = getCurrentInstance().proxy
    const { route, router } = useRouter()
    const { isProduction } = useProcessEnv()
    const currentClubMeta = inject('currentClubMeta', { today: null })
    const today = currentClubMeta.today || new Date().toISOString().slice(0, 10)
    const date = ref(route.value.query.date || today)
    // ステッパーの選択状態は「正準順序の固定値（数値）」で保持する（配列インデックス
    // ではない）。集計表の有無で steps が動的に増減しても各ステップの値が不変になり、
    // v-stepper-content の表示判定が崩れない（詳細は keyToStepValue のコメント参照）。
    const step = ref(0)

    // 本日サマリーの開閉（スマホ幅でのトグル用。タブレット以上は常時横並びで無関係）。
    const summaryOpen = ref(false)

    // 本日サマリーの再取得トリガー。値が変わる操作の後にインクリメントして、
    // リロードなしでサマリー（売上/現金/カード/諸経費/キャスト・ボーイ日払い）を最新化する。
    // 反映契機: セル保存（earning系BR=基本給/控除/歩合/日払い 含む）/ 一括集計完了 /
    //   タイムカード変更 / データセット管理の卓・売掛・伝票CRUD（Data.vue の changed）/
    //   経費の保存（ExpensesTable の changed）/ 画面復帰（別画面のレジ出金など）。
    //   ※ 販売履歴は会計額を変えないためサマリー対象外（B-1）。
    const summaryRefreshKey = ref(0)
    const bumpSummary = () => { summaryRefreshKey.value += 1 }

    // 経費管理ステップの「諸経費 / 諸入費」タブの選択状態（0: 諸経費, 1: 諸入費）。
    const expenseTabIndex = ref(0)

    // APIエラーハンドラ（既存 eod-closing と同じ handleApiError パターン）
    const onApiError = error => handleApiError(error, { $toast: vm.$toast })

    const {
      isLoading,
      loadingStatuses,
      jobTimestamps,
      jobErrorMessages,
      jobStatuses,
      availableUsers,
      userAggregationsData,
      tableAggregationsData,
      clubAggregationsData,
      userAggregationsMap,
      tableAggregationsMap,
      clubAggregationsMap,
      sheetStatus,
      getAggregations,
      bulkCompletionByType,
      bulkCompleteByType,
      bulkCompleteAllByType,
      individualCompletingIds,
      bulkCompleteAggregation,
      upstreamReadinessForBulkComplete,
      generateBusinessRuleResult,
      fetchAvailableUsers,
      createUserAggregation,
      destroyUserAggregation,
      fetchDailyDiagnostic,
    } = useEodClosingV2Aggregations(date, { handleApiError: onApiError })

    // ステッパー構成（動的）。
    // データセット管理・経費管理・完了は常に表示。
    // ユーザー集計／卓集計／店舗集計は、対応する集計表が 0 件のとき除外する。
    // ステッパー自体は読み込み完了（!isLoading）後にのみ描画するため（テンプレート側
    // で v-if 制御）、ここで isLoading を考慮する必要はない。これにより「開いた直後に
    // 全カテゴリを出しておき、あとで空のステップが消える」チラつきが起きず、確定した
    // 構成だけが最初から表示される。
    const steps = computed(() => {
      const list = [
        { key: 'dataset', label: 'データセット管理' },
        { key: 'expense', label: '経費管理' },
      ]

      if (userAggregationsData.value.length > 0) {
        list.push({ key: 'user', label: 'ユーザー集計' })
      }
      if (tableAggregationsData.value.length > 0) {
        list.push({ key: 'table', label: '卓集計' })
      }
      if (clubAggregationsData.value.length > 0) {
        list.push({ key: 'club', label: '店舗集計' })
      }

      list.push({ key: 'done', label: '完了' })

      return list
    })

    // ステップ key → ステッパーの値（数値）。配列インデックスではなく「正準順序での
    // 固定値」を使うのが要点。集計表の有無で steps が動的に増減しても、各ステップの値が
    // 不変になるため、v-stepper-content の :step が組み替えで変化して中身が空になる
    // 不具合（インデックスのズレ起因）を構造的に防げる。値は丸の表示番号も兼ねる。
    const STEP_KEYS = ['dataset', 'expense', 'user', 'table', 'club', 'done']
    const keyToStepValue = key => STEP_KEYS.indexOf(key)
    const stepValueToKey = value => STEP_KEYS[value]

    // --- ステップ（カテゴリ）単位の集計状態マーク ----------------------
    // カテゴリ内の集計表の状態を集約し、未集計 > 未保存 > 要再集計 > 完了 の優先で
    // 1 つの状態を返す（未集計が最も手前の状態）。
    //   - 'uncounted'           : 集計ジョブ未実行の集計表が 1 つでもある
    //   - 'unsaved'             : 未集計は無いが、未保存・失敗・集計中の集計表がある
    //   - 'needs_reaggregation' : 保存済みだが集計後にタイムカードが更新された
    //   - 'done'                : すべて保存済み
    //   - null                  : 集計表が無い（マーク非表示）
    const resolveCategoryStatus = list => {
      if (!list || list.length === 0) return null
      let hasUncounted = false
      let hasIncomplete = false
      let hasNeedsReaggregation = false
      list.forEach(agg => {
        const { id } = agg.attributes
        const jobStatus = jobStatuses.value[id]
        if (jobStatus === null || jobStatus === undefined) {
          hasUncounted = true

          return
        }
        const s = sheetStatus(id)
        if (s === 'needs_reaggregation') {
          hasNeedsReaggregation = true
        } else if (s !== 'saved') {
          hasIncomplete = true
        }
      })
      if (hasUncounted) return 'uncounted'
      if (hasIncomplete) return 'unsaved'
      if (hasNeedsReaggregation) return 'needs_reaggregation'

      return 'done'
    }

    const userStepStatus = computed(() => resolveCategoryStatus(userAggregationsData.value))
    const tableStepStatus = computed(() => resolveCategoryStatus(tableAggregationsData.value))
    const clubStepStatus = computed(() => resolveCategoryStatus(clubAggregationsData.value))

    // ステップ key -> カテゴリ集計のステータス（対象外ステップ・集計表なしは null）。
    const categoryStatus = key => ({
      user: userStepStatus.value,
      table: tableStepStatus.value,
      club: clubStepStatus.value,
    }[key] || null)

    // ステップ丸に表示するアイコン（done は既定のチェックを使うため null）。
    const categoryIcon = key => {
      const status = categoryStatus(key)

      return status ? (STEP_STATUS_ICON[status] || null) : null
    }

    // ステップ丸の色分け用クラス（カテゴリステップのみ付与）。
    // 共通の eod-v2-step--category と、ステータス別 eod-v2-step--<status> を返す。
    const categoryStatusClass = key => {
      const status = categoryStatus(key)

      return status ? ['eod-v2-step--category', `eod-v2-step--${status}`] : null
    }

    // カテゴリ集計（ユーザー/卓/店舗）が「後続へ進んでよい状態」か。
    // 保存済み（done）であることに加え、bulk_complete 実行中でないこと。
    // （done のまま再度「完了する」を押すと再集計が走るが、その集計中は done に
    //   見えてしまうため、bulk_complete の進捗も併せて見て後続をロックする）
    const CATEGORY_STEP_TYPE = {
      user: 'EodClosingUserAggregation',
      table: 'EodClosingTableAggregation',
      club: 'EodClosingClubAggregation',
    }
    const isCategoryReady = key => {
      const bulkRunning = bulkCompletionByType[CATEGORY_STEP_TYPE[key]]?.status === 'in_progress'

      return categoryStatus(key) === 'done' && !bulkRunning
    }

    // ステップの押下可否（クリックでの遷移可否）。
    // カテゴリ集計（ユーザー/卓/店舗）には依存順があり、後続ステップへは
    // 先行する全カテゴリ集計が完了していないと遷移できない。
    //   例) ユーザー集計が未集計/要再集計/集計中/再集計中 → 店舗集計・完了は押せない
    //       店舗集計が未保存 → 完了は押せない
    // データセット管理・経費管理や、先行カテゴリが無いステップは常に押せる。
    const isStepEditable = idx => steps.value
      .slice(0, idx)
      .filter(s => ['user', 'table', 'club'].includes(s.key))
      .every(s => isCategoryReady(s.key))

    // ロック中ステップにカーソルを合わせたときの案内文。
    // 先行する未完了カテゴリのうち最初のものを「先に○○を完了してください」と促す。
    const CATEGORY_STEP_LABEL = { user: 'ユーザー集計', table: '卓集計', club: '店舗集計' }
    const stepLockReason = idx => {
      if (isStepEditable(idx)) return null
      const blocking = steps.value
        .slice(0, idx)
        .filter(s => ['user', 'table', 'club'].includes(s.key))
        .find(s => !isCategoryReady(s.key))

      return blocking ? `先に${CATEGORY_STEP_LABEL[blocking.key]}を完了してください` : null
    }

    // 全カテゴリのクロージングが完了しているか（未集計・未保存が無い）。
    // 集計表が無いカテゴリ（null）は「やることなし」として完了扱い。
    const allClosingCompleted = computed(() => (
      [userStepStatus.value, tableStepStatus.value, clubStepStatus.value]
        .every(s => s === null || s === 'done')
    ))

    // 現在選択中ステップの配列インデックス（順序判定用）。
    // :complete の「現在より手前のステップか」判定など、順序が必要な箇所で使う。
    const currentStepIndex = computed(() => (
      steps.value.findIndex(s => keyToStepValue(s.key) === step.value)
    ))

    // トースト通知などから ?step=user|table|club|dataset|expense|done で開始ステップを
    // 指定された場合、対応するステップへ移動する。
    //
    // 読み込み中（isLoading）はデータの有無に関わらず全カテゴリを暫定表示するため、
    // 「卓集計のように実データが無く後で消えるステップ」へ誤って遷移しないよう、
    // 読み込み完了（!isLoading）まで適用を遅延する。
    const pendingStepKey = ref(route.value.query.step || null)
    const applyPendingStep = () => {
      if (!pendingStepKey.value || isLoading.value) return
      if (steps.value.some(s => s.key === pendingStepKey.value)) {
        step.value = keyToStepValue(pendingStepKey.value)
        pendingStepKey.value = null
      }
    }
    applyPendingStep()

    // ユーザーが手動でステップを押下した瞬間、?step= のディープリンク適用を打ち切る。
    // 描画中に別ステップを押したら、その操作を尊重してディープリンクで上書きしない。
    const cancelPendingStep = () => {
      pendingStepKey.value = null
    }

    // ステップ配列の変化・読み込み完了に追従する処理。
    // 1) ディープリンク（?step=）の適用（読み込み完了後）。isLoading は集計データ確定
    //    より後に false になるため、steps だけでなく isLoading も監視対象にしないと、
    //    ディープリンクが適用されない（読み込み完了の瞬間に再評価する必要がある）。
    // 2) 選択中ステップが steps から消えた場合のみ、先頭ステップへフォールバック。
    //    値は正準順序の固定値なので、ステップ増減でインデックスがズレても表示は壊れない。
    watch([steps, isLoading], () => {
      applyPendingStep()
      const newSteps = steps.value
      const exists = newSteps.some(s => keyToStepValue(s.key) === step.value)
      if (!exists) {
        step.value = newSteps[0] ? keyToStepValue(newSteps[0].key) : 0
      }
    })

    // 集計ステップ（ユーザー集計／卓集計／店舗集計）に切り替わった瞬間、
    // 「集計後にタイムカードが変更されたか」を診断 API で確認する。
    // データセット管理ステップでタイムカードを編集して戻ってきたケースで、
    // 「要再集計」表示を最新化するため。
    watch(step, value => {
      const key = stepValueToKey(value)
      if (key === 'user' || key === 'table' || key === 'club') {
        fetchDailyDiagnostic()
      }
    })

    // タイムカード／休憩が変更されたら、データ整合性診断を取り直して
    // 「要再集計」表示を即座に反映する。リロードや別ステップへの移動を待たず、
    // データセット管理ステップ上で更新が走ってもステッパー／集計ステップ側の
    // ステータスがすぐ切り替わる。
    const onTimecardsChanged = () => {
      fetchDailyDiagnostic()
      bumpSummary()
    }

    // 一括集計（bulk_complete）が完了したら本日サマリーを再取得。
    // 再集計で earning（社交/男子日払い）や売上・仕入が変わるため。
    watch(
      () => [
        bulkCompletionByType.EodClosingUserAggregation?.status,
        bulkCompletionByType.EodClosingTableAggregation?.status,
        bulkCompletionByType.EodClosingClubAggregation?.status,
      ],
      (now, prev) => {
        const justCompleted = now.some((s, i) => s === 'completed' && (prev || [])[i] !== 'completed')
        if (justCompleted) bumpSummary()
      },
    )

    // 上流（ユーザー集計・卓集計）が未保存・実行中のときの案内ダイアログ。
    // ステッパー自体が遷移手段を提供するため、ダイアログ内にリンクは持たない。
    const upstreamDialog = ref({ open: false, state: null })

    // 「完了する」（カテゴリ単位の bulk_complete）。完了済みはスキップし、未完了だけ実行。
    // 店舗集計（Club）のときだけ、フロント側で上流の保存状態を確認する。
    // ready → API 起動、in_progress / not_ready → 案内ダイアログ表示。
    const onBulkComplete = type => {
      const readiness = upstreamReadinessForBulkComplete(type)
      if (readiness.state !== 'ready') {
        upstreamDialog.value = { open: true, state: readiness.state }

        return
      }
      bulkCompleteByType(type)
    }

    // ⋮「完了済みも含めて全て実行」（force-all）。完了済みも reset→集計→保存し直す。
    // 上流確認は「完了する」と同様に適用する（店舗集計のみ）。
    const onBulkCompleteAll = type => {
      const readiness = upstreamReadinessForBulkComplete(type)
      if (readiness.state !== 'ready') {
        upstreamDialog.value = { open: true, state: readiness.state }

        return
      }
      bulkCompleteAllByType(type)
    }

    // 集計表単位の個別集計（リセット→集計→保存）。クロス型ゲートは不要
    // （上流の保存済み結果を読んで再計算するため）。当該シートのみ「集計中」になる。
    const onIndividualComplete = ({ aggregationId, type }) => {
      bulkCompleteAggregation(aggregationId, type)
    }

    // セル保存（generateBusinessRuleResult）後に本日サマリーを再取得。
    // earning系BR（基本給/控除/歩合/日払い）の保存は Earning（出金 transaction）を
    // 生成・更新するため、社交/男子日払いを含めサマリーが最新化される。
    const onSaveCell = async payload => {
      await generateBusinessRuleResult(payload)
      bumpSummary()
    }

    // 適用ユーザーの変更（ユーザー集計ステップから）
    const onFetchAvailableUsers = ({ id, callback }) => {
      fetchAvailableUsers(id, callback)
    }
    const onCreateUser = ({ id, userId, callback }) => {
      createUserAggregation(id, { userId, callback })
    }
    const onDestroyUser = ({
      aggregationId, id, userId, callback,
    }) => {
      destroyUserAggregation(aggregationId, { id, userId, callback })
    }

    // 日付変更時: 本番では未来日を弾き、URLクエリを同期し、集計を取り直す
    watch(date, newDate => {
      const dateObj = parseISO(newDate)
      const todayObj = parseISO(today)
      if (isProduction && isAfter(dateObj, todayObj)) {
        date.value = today

        return
      }

      if (route.value.query.date !== date.value) {
        router.replace({
          name: route.value.name,
          query: { ...route.value.query, date: date.value },
        })
      }
      getAggregations()
    })

    // 画面復帰（別タブ/別画面のレジ出金など、画面外で earning/売上/経費が変わった分）を
    // 拾うためのフォールバック。タブが再び表示されたら本日サマリーを再取得する。
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') bumpSummary()
    }

    onMounted(() => {
      getAggregations()
      document.addEventListener('visibilitychange', onVisibilityChange)
    })

    onBeforeUnmount(() => {
      document.removeEventListener('visibilitychange', onVisibilityChange)
    })

    return {
      date,
      today,
      step,
      currentStepIndex,
      keyToStepValue,
      summaryOpen,
      summaryRefreshKey,
      bumpSummary,
      expenseTabIndex,
      steps,
      categoryStatus,
      categoryIcon,
      categoryStatusClass,
      isStepEditable,
      stepLockReason,
      cancelPendingStep,
      allClosingCompleted,
      isProduction,

      isLoading,
      loadingStatuses,
      jobTimestamps,
      jobErrorMessages,
      jobStatuses,
      availableUsers,
      userAggregationsData,
      tableAggregationsData,
      clubAggregationsData,
      userAggregationsMap,
      tableAggregationsMap,
      clubAggregationsMap,
      sheetStatus,
      bulkCompletionByType,
      generateBusinessRuleResult,
      onSaveCell,
      onTimecardsChanged,
      onBulkComplete,
      onBulkCompleteAll,
      individualCompletingIds,
      onIndividualComplete,
      onFetchAvailableUsers,
      onCreateUser,
      onDestroyUser,
      upstreamDialog,

      icons: {
        mdiCalendar,
        mdiChevronDown,
        mdiChevronUp,
      },
    }
  },
}
</script>

<style lang="scss">
// 管理画面共通コンテナ（.app-content-container, mx-auto + max-width）の中央寄せにより
// 左に大きな余白が出るため、このクロージング新ページを含むときだけ中央寄せ・最大幅を
// 解除して左に詰める（本日サマリーバーを画面左寄りに配置するため）。
.app-content-container:has(> .eod-closing-v2-page) {
  max-width: none;
}

// 読み込み中スケルトン（ステッパー＋集計表の最終形を模す）。
.eod-closing-v2-loading {
  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
  }

  &__step {
    display: flex;
    align-items: center;
    gap: 12px;
    flex: 1 1 0;
    min-width: 0;
  }

  // 丸（avatar）は固定サイズに、ラベル（text）は残り幅いっぱいに。
  &__step .v-skeleton-loader__avatar {
    width: 32px;
    height: 32px;
    flex: 0 0 auto;
  }

  &__step-label {
    flex: 1 1 auto;
    min-width: 0;
  }
}

// 左に本日サマリーバー、右にクロージング本体を並べる。
.eod-closing-v2-page {
  display: flex;
  align-items: flex-start;
  position: relative; // モバイル時にサマリーをオーバーレイ配置する基準

  // スマホ幅用トグル。タブレット以上では非表示（常時横並びのため不要）。
  &__summary-toggle {
    display: none;
  }

  &__summary {
    flex: 0 0 200px;
    width: 200px;
    align-self: flex-start;
    position: sticky;
    top: 16px;
    height: calc(100vh - 32px);
    overflow-y: auto;
    border-right: 1px solid rgba(255, 255, 255, .12);

    // スクロールバーは表示しない（縦スクロールは可能）。
    scrollbar-width: none; // Firefox
    -ms-overflow-style: none; // IE/Edge(旧)

    &::-webkit-scrollbar {
      display: none; // Chrome/Safari
    }
  }

  &__content {
    flex: 1 1 auto;
    min-width: 0;
    padding-left: 24px;
  }
}

// タブレット幅以下では縦積みにし、本日サマリーはトグルで開閉する。
// （デスクトップ幅のみ横並びを維持する）
@media (max-width: 960px) {
  .eod-closing-v2-page {
    flex-direction: column;

    &__summary-toggle {
      display: flex;
      align-items: center;
      width: 100%;
      padding: 10px 14px;
      margin-bottom: 8px;
      background: rgba(255, 255, 255, .04);
      border: 1px solid rgba(255, 255, 255, .12);
      border-radius: 8px;
      color: #fff;
      font-size: 14px;
      font-weight: 600;
      cursor: pointer;
    }

    // 本体（タイトル・日付・ステップ・集計表）の上に重ねるオーバーレイとして配置。
    // トグルボタンの直下から、フローティングパネルとして出す。
    // 開閉は opacity / transform でフェード（左サイドバーのように滑らかに）。
    &__summary {
      position: absolute;
      top: 52px; // トグルボタンの高さ分だけ下げる
      left: 0;
      right: 0;
      // サイドバー(6)・時点レジ金などのメニュー(8)・ヘッダー(5)より下、
      // ページ本文より上に重ねる。
      z-index: 4;
      width: auto;
      height: auto;
      max-height: calc(100vh - 140px);
      overflow-y: auto;
      background: rgb(40, 36, 61); // .v-application の背景色に合わせて下を隠す
      border: 1px solid rgba(255, 255, 255, .12);
      border-radius: 10px;
      box-shadow: 0 16px 40px rgba(0, 0, 0, .55);

      // 閉じている状態（フェードアウト後）
      opacity: 0;
      visibility: hidden;
      transform: translateY(-8px);
      pointer-events: none;
      transition: opacity .22s ease, transform .22s ease, visibility .22s;
    }

    // 開いたときフェードイン
    &--summary-open &__summary {
      opacity: 1;
      visibility: visible;
      transform: translateY(0);
      pointer-events: auto;
    }

    &__content {
      padding-left: 0;
    }
  }
}

.eod-closing-v2-stepper {
  background: none !important;

  // ステッパー行と下のステップ内容の間隔を広げる
  .v-stepper__items {
    margin-top: 16px;
  }

  // タブレット・スマホ幅ではステップが潰れる（特に「完了」）ため、
  // ヘッダーを折り返さず横スクロール可にし、各ステップは自然な幅を保つ。
  @media (max-width: 960px) {
    overflow: visible;

    .v-stepper__header {
      flex-wrap: nowrap;
      overflow-x: auto;
      justify-content: flex-start;
    }

    .v-stepper__step {
      flex: 0 0 auto;
      padding: 16px 10px;
    }

    .v-stepper__label {
      white-space: nowrap;
    }

    .v-stepper__header .v-divider {
      min-width: 24px;
    }
  }

  // データセット管理・経費管理・完了など（カテゴリ以外）の完了済みステップは
  // 緑アウトライン + 緑チェックで表示する。
  // 丸には Vuetify の .primary クラスが付き border-color: 紫 !important が当たるため、
  // border-color も !important で上書きする。
  .v-stepper__step--complete:not(.eod-v2-step--category) .v-stepper__step__step {
    background-color: transparent !important;
    border: 2px solid #4caf50 !important;
    color: #4caf50 !important;

    .v-icon {
      color: #4caf50 !important;
    }
  }

  // カテゴリステップ（ユーザー/卓/店舗集計）はステータスで丸の色とアイコンを変える。
  // アウトライン + ヨナレジテーマ色（CSS 変数）で表示する。
  .eod-v2-step--category .v-stepper__step__step {
    background-color: transparent !important;
    border-width: 2px;
    border-style: solid;
  }

  // 未集計 → 赤（ビックリマーク）
  .eod-v2-step--uncounted .v-stepper__step__step {
    border-color: var(--v-error-base) !important;
    color: var(--v-error-base) !important;

    .v-icon {
      color: var(--v-error-base) !important;
    }
  }

  // 未保存・要再集計 → 黄（ワーニング）
  .eod-v2-step--unsaved .v-stepper__step__step,
  .eod-v2-step--needs_reaggregation .v-stepper__step__step {
    border-color: var(--v-warning-base) !important;
    color: var(--v-warning-base) !important;

    .v-icon {
      color: var(--v-warning-base) !important;
    }
  }

  // 完了 → 緑（アイコンは既定のチェックのまま）
  .eod-v2-step--done .v-stepper__step__step {
    border-color: #4caf50 !important;
    color: #4caf50 !important;

    .v-icon {
      color: #4caf50 !important;
    }
  }

  // ツールチップ用ラッパー。元のステップと同じく flex 配置にしてレイアウトを保つ。
  .eod-v2-step-activator {
    display: flex;
    align-items: center;
  }

  // 押せない（依存ロック中の）ステップは薄く表示してクリック不可を明示する。
  // editable でない & アクティブでもないステップが対象。
  .v-stepper__step:not(.v-stepper__step--editable):not(.v-stepper__step--active) {
    opacity: .4;
  }
}

// ステップのロック案内ツールチップ。本文を少し大きく・読みやすくする。
// v-tooltip の内容は body 直下（.v-application）へ出るため、ステッパーの
// スコープ外（トップレベル）で指定する。
.v-tooltip__content.eod-v2-step-tooltip {
  font-size: 14px;
  font-weight: 600;
  line-height: 1.6;
  padding: 8px 14px;
  opacity: 1 !important;
}
</style>
